'use client';

import { useState } from 'react';
import { EMAIL_PREFIXES } from '../lib/types';
import EmailPreview from './EmailPreview';

interface Step4Props {
  domain: string;
  selectedEmails: string[];
  onToggle: (prefix: string) => void;
  onAddCustom: (prefix: string) => void;
  onRemoveCustom: (prefix: string) => void;
  onNext: () => void;
  onBack: () => void;
}

const PREFIX_REGEX = /^[a-z0-9][a-z0-9._-]*[a-z0-9]$/;
const PREDEFINED = EMAIL_PREFIXES.map((p) => p.value as string);

export default function Step4EmailSimulator({
  domain,
  selectedEmails,
  onToggle,
  onAddCustom,
  onRemoveCustom,
  onNext,
  onBack,
}: Step4Props) {
  const [customInput, setCustomInput] = useState('');
  const [customError, setCustomError] = useState('');

  const customEmails = selectedEmails.filter((e) => !PREDEFINED.includes(e));

  function handleAddCustom() {
    const value = customInput.toLowerCase().replace(/\s/g, '').replace(/@.*$/, '');
    if (!value) return;

    if (!PREFIX_REGEX.test(value) || value.length < 2) {
      setCustomError('Format invalide. Utilisez uniquement lettres, chiffres, points et tirets.');
      return;
    }

    if (selectedEmails.includes(value)) {
      setCustomError('Ce préfixe est déjà ajouté.');
      return;
    }

    setCustomError('');
    setCustomInput('');
    onAddCustom(value);
  }

  return (
    <div className="animate-fade-in max-w-2xl mx-auto">
      <h2 className="text-2xl font-bold mb-2">Configurez vos adresses email</h2>
      <p className="text-muted mb-6">
        Sélectionnez les adresses que vous souhaitez créer pour{' '}
        <span className="font-mono text-primary font-medium">{domain}</span>
      </p>

      {/* Predefined prefixes */}
      <p className="text-sm font-medium mb-3">Adresses standards</p>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
        {EMAIL_PREFIXES.map((prefix, i) => {
          const isSelected = selectedEmails.includes(prefix.value);
          const email = `${prefix.value}@${domain}`;

          return (
            <button
              key={prefix.value}
              onClick={() => onToggle(prefix.value)}
              className={`flex items-start gap-3 p-3.5 rounded-lg border-2 transition-all duration-200 text-left cursor-pointer animate-slide-in ${
                isSelected
                  ? 'border-primary bg-primary-light/50'
                  : 'border-border hover:border-primary/30'
              }`}
              style={{ animationDelay: `${i * 40}ms` }}
            >
              <div
                className={`w-5 h-5 rounded border-2 flex items-center justify-center shrink-0 mt-0.5 transition-colors ${
                  isSelected
                    ? 'border-primary bg-primary text-white'
                    : 'border-border'
                }`}
              >
                {isSelected && <span className="text-xs">&#10003;</span>}
              </div>
              <div className="min-w-0">
                <p className="font-mono text-sm font-medium truncate">{email}</p>
                <p className="text-xs text-muted mt-0.5">{prefix.description}</p>
              </div>
            </button>
          );
        })}
      </div>

      {/* Custom prefixes */}
      <div className="mt-8 pt-6 border-t border-border">
        <p className="text-sm font-medium mb-1">Adresses personnalisées</p>
        <p className="text-xs text-muted mb-4">
          Ajoutez des adresses nominatives ou spécifiques (ex: jeancodjo, comptabilite, ceo...)
        </p>

        <div className="flex gap-2">
          <div className="flex-1 relative">
            <input
              type="text"
              value={customInput}
              onChange={(e) => {
                setCustomInput(e.target.value.toLowerCase().replace(/\s/g, ''));
                setCustomError('');
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  handleAddCustom();
                }
              }}
              placeholder="jeancodjo"
              className="w-full pl-4 pr-24 py-3 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent font-mono text-sm bg-white"
            />
            <span className="absolute right-3 top-1/2 -translate-y-1/2 text-muted text-sm font-mono pointer-events-none">
              @{domain}
            </span>
          </div>
          <button
            onClick={handleAddCustom}
            disabled={!customInput.trim()}
            className="px-5 py-3 bg-primary hover:bg-primary-hover disabled:opacity-40 disabled:cursor-not-allowed text-white font-medium text-sm rounded-lg transition-colors cursor-pointer shrink-0"
          >
            Ajouter
          </button>
        </div>

        {customError && (
          <p className="text-xs text-danger mt-2">{customError}</p>
        )}

        {/* Custom emails list */}
        {customEmails.length > 0 && (
          <div className="mt-4 space-y-2">
            {customEmails.map((prefix) => (
              <div
                key={prefix}
                className="flex items-center justify-between px-4 py-2.5 bg-primary-light/50 border-2 border-primary rounded-lg animate-fade-in"
              >
                <div className="flex items-center gap-2 min-w-0">
                  <span className="text-success text-sm">&#10003;</span>
                  <span className="font-mono text-sm font-medium truncate">
                    {prefix}@{domain}
                  </span>
                  <span className="text-[10px] bg-primary/10 text-primary px-1.5 py-0.5 rounded font-medium shrink-0">
                    personnalisé
                  </span>
                </div>
                <button
                  onClick={() => onRemoveCustom(prefix)}
                  className="text-muted hover:text-danger transition-colors cursor-pointer ml-2 shrink-0 p-1"
                  title="Supprimer"
                >
                  &#10005;
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      <EmailPreview domain={domain} emails={selectedEmails} />

      <div className="flex gap-3 mt-8">
        <button
          onClick={onBack}
          className="px-6 py-3 rounded-lg border border-border text-muted hover:bg-gray-50 transition-colors cursor-pointer"
        >
          Retour
        </button>
        <button
          onClick={onNext}
          disabled={selectedEmails.length === 0}
          className="flex-1 bg-primary hover:bg-primary-hover disabled:opacity-40 disabled:cursor-not-allowed text-white font-semibold py-3 rounded-lg transition-colors cursor-pointer"
        >
          Voir le récapitulatif
        </button>
      </div>
    </div>
  );
}